import { useCallback } from "react";
import { useSegment } from "./useSegment";
import { EventName } from "../constants/events";
import { useDebugLogger } from "./useDebugLogger";
import { rumProvider } from "rum-provider";

/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
export interface AnalyticsTracking {
  publishEvent: (
    eventName: EventName,
    properties?: Record<string, unknown>,
  ) => Promise<void>;
  trackError: (message: string, context: unknown) => Promise<void>;
  clearAnalyticsSession: () => Promise<void>;
}

const SENSITIVE_KEY_PATTERN =
  /authorization|token|secret|password|cookie|header|config|request|response|body|payload|data|ssn|socialsecuritynumber|dateofbirth|dob|ein|address/i;
const MAX_CONTEXT_DEPTH = 3;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const sanitizeContextValue = (
  value: unknown,
  depth: number,
): unknown | undefined => {
  if (value == null) {
    return value;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (depth >= MAX_CONTEXT_DEPTH) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const sanitizedItems = value
      .map((item) => sanitizeContextValue(item, depth + 1))
      .filter((item): item is unknown => item !== undefined);

    return sanitizedItems.length ? sanitizedItems : undefined;
  }

  if (!isRecord(value)) {
    return String(value);
  }

  const sanitizedEntries = Object.entries(value).reduce<Record<string, unknown>>(
    (accumulator, [key, nestedValue]) => {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        return accumulator;
      }

      const sanitizedNestedValue = sanitizeContextValue(nestedValue, depth + 1);
      if (sanitizedNestedValue !== undefined) {
        accumulator[key] = sanitizedNestedValue;
      }

      return accumulator;
    },
    {},
  );

  return Object.keys(sanitizedEntries).length ? sanitizedEntries : undefined;
};

export const sanitizeTrackingContext = (
  context: unknown,
): Record<string, unknown> | undefined => {
  if (!isRecord(context)) {
    return undefined;
  }

  const sanitizedContext = sanitizeContextValue(context, 0);
  return isRecord(sanitizedContext) ? sanitizedContext : undefined;
};

/**
 * Helper function to ensure an argument is non-null and the resulting function doesn't throw any exceptions.
 * Used by analytics, which are nice to have but shouldn't break the main site if something goes wrong.
 * @param Provider The object to confirm is non-null before continuing
 * @param func The function to apply to the provider argument, and wrap
 */
async function dontThrow<
  T,
  F extends (_: T) => void | Promise<void> | Promise<unknown>,
>(provider: T | null, func: F): Promise<unknown> {
  if (!provider) {
    return;
  }

  try {
    return await func(provider);
  } catch (ex) {
    console.error("In tracking analytics", ex, provider);
  }
}

export const useTracking = (): AnalyticsTracking => {
  const logger = useDebugLogger();
  const segment = useSegment();

  const publishEvent = useCallback(
    async (
      eventName: EventName,
      properties?: Record<string, unknown>,
    ): Promise<void> => {
      logger.log("Publish event", { eventName, properties });

      await Promise.all([
        dontThrow(rumProvider, (r) => r.trackAction(eventName, properties)),
        dontThrow(segment, async (s) => await s.track(eventName, properties)),
      ]);
    },
    [logger, segment],
  );

  const trackError = useCallback(
    async (message: string, context: unknown) => {
      const sanitizedContext = sanitizeTrackingContext(context);

      logger.log("error", {
        errorMessage: message,
        context: sanitizedContext,
      });

      await Promise.all([
        dontThrow(rumProvider, (r) =>
          r.trackError(message, {
            context: sanitizedContext,
          }),
        ),

        dontThrow(
          segment,
          async (s) =>
            await s.track("error", {
              errorMessage: message,
              context: sanitizedContext,
            }),
        ),
      ]);
    },
    [logger, segment],
  );

  const clearAnalyticsSession = useCallback(async () => {
    logger.log("Clearing analytics context");

    await Promise.all([
      dontThrow(rumProvider, (r) => r.endSession()),
      dontThrow(segment, async (s) => await s.reset()),
    ]);
  }, [logger, segment]);

  return {
    publishEvent,
    trackError,
    clearAnalyticsSession,
  };
};
